import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useNavigate, useParams } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { ProviderInvoicePaymentApi, ProviderInvoicePaymentDataType } from "@/api/ProviderInvoicePaymentApi";
import { formatMoneyValue } from "@utils/Currency";
import { showAlert } from "@utils/Alert";
import { ProviderInvoiceApi, ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import { Col, Container, Row } from "reactstrap";
import { ProviderInvoiceFormDataKeys } from "@/generated/ProviderInvoiceFormDataKeys";
import { ProviderInvoiceRelatedAttributes } from "@/formTypes/ProviderInvoiceDataType";
import { formatDate } from "@utils/Api";
import LoadingScreen from "@components/content/LoadingScreen";
import { getPaymentMethodLabel } from "@utils/Utils";
import { FetchResponse } from "@/api/ApiClient";
import { number } from "mathjs";
import { ProviderNoteDataType } from "@/api/ProviderNoteApi";
import { INVOICE_PAYMENT_CANCELLED_STATUS, PROVIDER_CREDITNOTE_TYPE_CODE } from "@utils/Constants";

const ProviderInvoicePaymentIndex = () => {
  const navigate = useNavigate();
  const params = useParams();
  const modelRef = useRef<ModelIndexHandle>(null);
  const { id: providerInvoiceId } = params;
  const [providerInvoice, setProviderInvoice] = useState<ProviderInvoiceDataType>({})
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paymentAmount, setPaymentAmount] = useState<Number>(Number(0));
  const [pendingAmount, setPendingAmount] = useState<Number>(Number(0));
  const [notesAmount, setNotesAmount] = useState<Number>(Number(0));
  const [paymentList, setPaymentList] = useState<Array<ProviderInvoicePaymentDataType>>([]);

  useEffect(() => {
    if (providerInvoiceId) {
      const run = async (providerInvoiceId: string) => {
        try {
          const invoices = await ProviderInvoiceApi.get(providerInvoiceId, {
            selectionSet: [
              ...ProviderInvoiceFormDataKeys.filter(k => !ProviderInvoiceRelatedAttributes.includes(k)),
              "currency.*",
              "provider.*",
              "providerNotes.*",
              "providerInvoicePayments.*",
              "providerInvoicePayments.currency.*",
            ]
          });
          setProviderInvoice(invoices);
          setTimeout(() => {
            modelRef.current?.refetch()
          });
        } finally {
          setIsLoading(false)
        }
      }
      run(providerInvoiceId);
      setIsLoading(true)
    }
  }, [providerInvoiceId]);

  useEffect(() => {
    calculatePaymentTotal(paymentList)
  }, [paymentList])

  const deleteRecord = async (id: string) => {
    const { errors } = await ProviderInvoicePaymentApi.delete(id);
    if (errors?.length) {
      showAlert('danger', 'El abono de la factura de provedor no puedo ser eliminado');
    } else {
      showAlert('success', 'El abono de la factura de provedor fue eliminado correctamente');
    }
    modelRef.current?.refetch();
  }

  const calculatePaymentTotal = (paymentList: Array<ProviderInvoicePaymentDataType>) => {
    let invoiceTotal = Number(0);
    let notesAmount = Number(0);
    let paymentAmount = Number(0);
    if (paymentList && paymentList.length > 0) {
      paymentAmount = paymentList.reduce((a, p) => {
        const amount = Number(p.monto)
        return a + amount;
      }, Number(0))
    }
    setPaymentAmount(paymentAmount)

    if (providerInvoice.providerNotes && providerInvoice.providerNotes?.length > 0) {
      notesAmount = providerInvoice.providerNotes?.reduce((a: number, p: ProviderNoteDataType) => {
        let amount = Number(p.total)
        if (p.tipo == `${PROVIDER_CREDITNOTE_TYPE_CODE}`) {
          amount = -1 * amount
        }
        return a + amount;
      }, Number(0))
    }
    setNotesAmount(notesAmount)

    if (providerInvoice.total) {
      invoiceTotal = providerInvoice.total
    }

    setPendingAmount(invoiceTotal - paymentAmount - notesAmount)
  }

  const getInvoicePayments = async (): Promise<FetchResponse<ProviderInvoicePaymentDataType>> => {
    // Los abonos anulados (por anular el recibo que los genero) no se listan ni suman.
    // Criterio "no cancelado": los abonos historicos no tienen `estado`.
    return {
      items: (providerInvoice.providerInvoicePayments || [])
        .filter((p: ProviderInvoicePaymentDataType) => p.estado != INVOICE_PAYMENT_CANCELLED_STATUS)
    }
  }

  return (
    <PageContainer
      title="Lista Abonos de Factura de Provedor"
      backUrl="/user/provider-invoices"
      primaryActions={[
        {
          onClick: () => {
            navigate(`/user/provider-invoices/payments/add/${providerInvoiceId}`)
          },
          actionTitle: "Nuevo",
          color: "success"
        }
      ]}
    >
      <Container fluid>
        <Row>
          <Col xs="2" md="2">
            <h5><b>Provedor:</b></h5>
          </Col>
          <Col>
            <h5><b>{providerInvoice?.provider?.nombre}</b></h5>
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Fecha:</b>
          </Col>
          <Col>
            {formatDate(providerInvoice?.fecha)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Numero Factura:</b>
          </Col>
          <Col>
            {providerInvoice?.numero}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Subtotal:</b>
          </Col>
          <Col>
            {formatMoneyValue(providerInvoice?.subtotal, providerInvoice?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Descuento:</b>
          </Col>
          <Col>
            {formatMoneyValue(providerInvoice?.descuento, providerInvoice?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Impuestos:</b>
          </Col>
          <Col>
            {formatMoneyValue(providerInvoice?.impuestos, providerInvoice?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <h5><b>Total:</b></h5>
          </Col>
          <Col>
            <h5><b>{formatMoneyValue(providerInvoice?.total, providerInvoice?.currency?.currency_id)}</b></h5>
          </Col>
        </Row>
        <hr />
      </Container>
      <ModelIndex<ProviderInvoicePaymentDataType>
        mandatoryFilters={[{ providerInvoiceId: { eq: providerInvoiceId } }]}
        messages={{
          noResults: "No hay abonos para este factura"
        }}
        ref={modelRef}
        onDataFetched={(data) => setPaymentList(data)}
        fetchData={() => getInvoicePayments()}
        primaryProperties={[
          {
            key: () => "Forma de Pago",
            highlighted: true
          },
          {
            key: (d) => { return getPaymentMethodLabel(d.forma_pago) },
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "2", xs: "4" },
            { sm: "8", xs: "8" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              // `created` lo estampan los abonos creados desde un recibo; los de la
              // pantalla manual solo tienen el `createdAt` de Amplify.
              return formatDate(data.created || data.createdAt)
            },
            title: "Fecha",
          },
          {
            key: "cobrador",
            title: "Cobrador",
          },
          {
            key: "recibo",
            title: "Recibo",
          },
          {
            key: (data) => {
              return formatMoneyValue(data.monto, data.currency.currency_id)
            },
            title: "Total",
            highlighted: true
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "3", md: "2" },
            { sm: "3", md: "2" },
            { sm: "3", md: "2" },
            { sm: "3", md: "2" }
          ]
        }
        listActions={[
          {
            href: `/user/provider-invoices/payments/edit/{:id}/${providerInvoiceId}`,
            actionTitle: "Editar Abono",
            iconProps: { icon: faEdit }
          },
          {
            actionTitle: "Eliminar Abono",
            iconProps: { icon: faTrash },
            needConfirm: true,
            confirmProps: {
              title: "Eliminar Abono",
              confirmMessage: "¿Seguro que desea eliminar esta Abono?",
              confirmBtnText: "Eliminar"
            },
            onClick: (id: string) => { deleteRecord(id) }
          }
        ]}
      >
      </ModelIndex>
      <hr />
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Total:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(providerInvoice?.total, providerInvoice?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Total Notas:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(notesAmount, providerInvoice?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Total Abonos:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(paymentAmount, providerInvoice?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Saldo:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(pendingAmount, providerInvoice?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <LoadingScreen isLoading={isLoading} />
    </PageContainer >
  );
}

export default ProviderInvoicePaymentIndex;
